/**
 * Test Burnout. Copenhagen Burnout Inventory (Personal Burnout subscale)
 * Kristensen TS et al., Work & Stress 2005;19(3):192-207. DOI 10.1080/02678370500297720
 * Domeniu public. Adaptare RO Mindtale revizuita de psihoterapeuti COPSI.
 */

import type { TestConfig } from "./types";

export type BurnoutCategory = "low" | "moderate" | "severe";

const QUESTIONS = [
  { id: 1, text: "Cât de des te simți obosit/ă?" },
  { id: 2, text: "Cât de des te simți epuizat/ă fizic?" },
  { id: 3, text: "Cât de des te simți epuizat/ă emoțional?" },
  { id: 4, text: 'Cât de des te gândești "nu mai pot"?' },
  {
    id: 5,
    text: "Cât de des te simți drenat/ă (ca și cum nu mai ai resurse)?",
  },
  {
    id: 6,
    text: "Cât de des te simți slăbit/ă și predispus/ă la îmbolnăviri?",
  },
];

const OPTIONS = [
  { label: "Mereu", value: 100 },
  { label: "Adesea", value: 75 },
  { label: "Câteodată", value: 50 },
  { label: "Rareori", value: 25 },
  { label: "Niciodată sau aproape niciodată", value: 0 },
];

export const burnoutConfig: TestConfig<BurnoutCategory> = {
  slug: "burnout",
  totalQuestions: QUESTIONS.length,
  estimatedMinutes: 2,
  hero: {
    eyebrow: "Test validat clinic · 2 minute · gratuit",
    titlePart1: "Cât de epuizat\nești,",
    italicText: "cu adevărat",
    titlePart2: "?",
    subtitle:
      "Șase întrebări simple. Niciun diagnostic, doar perspectivă. Răspunde sincer, te ajută să vezi ce nu mai poți ignora.",
    badges: [
      { text: "Copenhagen Burnout Inventory" },
      { text: "Revizuit de psihoterapeuți COPSI" },
      { text: "100% anonim" },
    ],
    imageSrc: "/teste/burnout/hero-portrait.webp",
    imageAlt:
      "Femeie tânără cu pulover crem ținând o cană de cafea, lumină blândă de dimineață",
    quote: {
      text: "Nu trebuie să fii la pământ ca să meriți o pauză.",
      attribution: "",
    },
  },
  questions: QUESTIONS,
  options: OPTIONS,
  scoring: {
    type: "average",
    scoreMax: 100,
    scoreSuffix: "/100",
    allowedAnswerValues: [0, 25, 50, 75, 100],
    categorize: (answers) => {
      if (answers.length !== QUESTIONS.length) {
        throw new Error("Toate întrebările trebuie completate");
      }
      const sum = answers.reduce((acc, val) => acc + val, 0);
      const score = Math.round(sum / answers.length);
      let category: BurnoutCategory;
      if (score < 50) category = "low";
      else if (score < 75) category = "moderate";
      else category = "severe";
      return { score, category };
    },
  },
  bands: [
    {
      category: "low",
      range: "0–49",
      label: "Burnout scăzut",
      caption: "Resursele țin pasul cu cerințele zilei.",
      bg: "#7DA86F",
    },
    {
      category: "moderate",
      range: "50–74",
      label: "Burnout moderat",
      caption: "E un semnal de atenție.",
      bg: "#DC7050",
    },
    {
      category: "severe",
      range: "75–100",
      label: "Burnout sever",
      caption: "Recomandat consult cu un specialist.",
      bg: "#B85037",
    },
  ],
  resultCopy: {
    low: {
      headline: "Ești în zonă bună.",
      subheadline: "Energia ta e încă acolo.",
      description:
        "Scorul tău arată că, la momentul ăsta, te descurci. Nu înseamnă că ești invincibil, doar că resursele tale țin pasul cu cerințele zilei.",
      meaning:
        "Burnout scăzut conform Copenhagen Burnout Inventory. Nu ești în pericol imediat de epuizare, dar conștientizarea e primul pas pentru a rămâne așa.",
      actions: [
        "Salvează acest test. Reia-l peste o lună, schimbările lente sunt cele mai importante de văzut.",
        'Observă-te când zici „nu mai pot" mai des decât de obicei. E semnal precoce.',
        "Păstrează ritualurile mici care îți dau energie (somn, mișcare, pauze).",
      ],
      ctaLabel: "Salvează testul pentru luna viitoare",
      ctaTone: "soft",
      showTherapistMatch: false,
    },
    moderate: {
      headline: "E un semnal.",
      subheadline: "Ai nevoie de o pauză, nu de mai multă cafea.",
      description:
        "Scorul tău arată că te epuizezi mai repede decât te recuperezi. Nu e dramatic, dar e o linie pe care merită să nu o mai depășești.",
      meaning:
        "Burnout moderat conform Copenhagen Burnout Inventory. Cercetările arată că, fără intervenție, scorul crește în 3-6 luni la majoritatea persoanelor în această zonă.",
      actions: [
        "Identifică UN lucru care îți drenează cel mai mult energia săptămâna asta. Vezi dacă poți reduce sau renegocia.",
        "Programează o oră în săptămână fără ecrane, fără agendă, fără productivitate. Singur/ă cu tine.",
        "Vorbește cu cineva care te ascultă fără să-ți dea soluții. Prieten, partener, terapeut.",
      ],
      ctaLabel: "Vezi terapeuți recomandați pentru tine",
      ctaTone: "warm",
      showTherapistMatch: true,
    },
    severe: {
      headline: "Ești epuizat/ă mai mult decât e ok.",
      subheadline: "Nu trebuie să rezolvi singur/ă.",
      description:
        "Scorul tău arată epuizare semnificativă. Asta înseamnă că rezervele tale fizice și emoționale sunt la pământ, nu pentru că ești slab/ă, ci pentru că ai dus mai mult decât poate duce un om sănătos.",
      meaning:
        "Burnout sever conform Copenhagen Burnout Inventory. Persoanele cu scor în această zonă au risc crescut de probleme de sănătate fizice (sistem imunitar slăbit, tensiune, tulburări de somn) și mintale (depresie, anxietate clinică). Recuperarea e posibilă, dar rar singur/ă.",
      actions: [
        "Primul pas: vorbește cu un terapeut. O ședință de probă te ajută să vezi unde stai exact.",
        "Spune cuiva apropiat ce simți. Singurătatea în burnout e cel mai mare amplificator.",
        "Dacă ai gânduri întunecate sau te simți în criză, sună la 112, disponibil non-stop. TelVerde Antisuicid, 0800 801 200, e gratuit, dar răspunde doar vineri-duminică, între 19:00 și 07:00.",
      ],
      ctaLabel: "Găsește terapeutul potrivit",
      ctaTone: "urgent",
      showTherapistMatch: true,
    },
  },
  source: {
    name: "Copenhagen Burnout Inventory (CBI). Personal Burnout subscale",
    citationTitle:
      "The Copenhagen Burnout Inventory: A new tool for the assessment of burnout.",
    authors: "Kristensen TS, Borritz M, Villadsen E, Christensen KB",
    journal: "Work & Stress",
    year: 2005,
    volume: "19(3):192-207",
    doi: "10.1080/02678370500297720",
    doiUrl: "https://doi.org/10.1080/02678370500297720",
    license: "Domeniu public",
    subsetNote:
      'Folosim doar subscala Personal Burnout (6 itemi) din CBI complet (19 itemi). Vezi mai sus secțiunea „De ce 6 și nu 19?" pentru detalii.',
  },
  validity: {
    cronbachAlpha: 0.87,
    testRetestR: 0.85,
    convergent: { measureName: "MBI-Emotional Exhaustion", r: 0.72 },
    sampleSize: 1914,
    countriesValidated: 30,
  },
  ui: {
    primaryAccent: "#2C969B",
    badgeDot: "#2C969B",
  },
  email: {
    subjectTemplate: (score, scoreMax, label) =>
      `Rezultatul tău: ${score}/${scoreMax} (${label})`,
    categoryStyles: {
      low: {
        label: "Burnout scăzut",
        range: "0–49",
        accent: "#5D8451",
        barBg: "#E8F0EA",
        barFg: "#264653",
        ctaLabel: "Explorează resursele noastre",
        ctaUrl:
          "https://mindtale.ro/resurse?utm_source=email&utm_medium=transactional&utm_campaign=test_burnout",
      },
      moderate: {
        label: "Burnout moderat",
        range: "50–74",
        accent: "#B85037",
        barBg: "#FBEAD5",
        barFg: "#264653",
        ctaLabel: "Vezi terapeuți recomandați",
        ctaUrl:
          "https://mindtale.ro/gaseste-terapeut?energy=2&utm_source=email&utm_medium=transactional&utm_campaign=test_burnout",
      },
      severe: {
        label: "Burnout sever",
        range: "75–100",
        accent: "#B85037",
        barBg: "#2D3F4A",
        barFg: "#FFF9F5",
        ctaLabel: "Găsește terapeutul potrivit",
        ctaUrl:
          "https://mindtale.ro/gaseste-terapeut?energy=1&utm_source=email&utm_medium=transactional&utm_campaign=test_burnout",
      },
    },
  },
  emailStarter: {
    title: "Ce poți face, indiferent de scor",
    intro:
      "Burnout-ul are 3 fețe (epuizare, distanțare sau cinism, și scăderea sentimentului că mai ești bun la ce faci). Exercițiile de mai jos ajută la oricare dintre ele. Alege unul și fă-l azi.",
    exercises: [
      {
        name: "Completează ciclul stresului",
        how: "Stresul e un ciclu fiziologic, separat de cauza lui. Închide-l chiar dacă problema rămâne: 20 de minute de mișcare care-ți crește pulsul, sau respirație lentă, sau o îmbrățișare lungă de 20 de secunde. Corpul primește semnalul că a trecut pericolul.",
      },
      {
        name: "Ritual de închidere a zilei",
        how: "Seara, 10 minute: notează ce ai lăsat nefăcut (ca să nu-l mai ții în cap), închide laptopul fizic, fă o plimbare scurtă. Te deconectezi mental de muncă, iar asta prezice energia de a doua zi.",
      },
      {
        name: "Protejează somnul 3 nopți la rând",
        how: "Țintește 7-8 ore, cu ecranul lăsat jos cu o oră înainte de culcare. Privarea de somn amplifică toate cele 3 fețe ale burnout-ului. E pârghia cu cel mai mare efect, cel mai repede.",
      },
    ],
    suggestions: [
      "Spune un „nu” pe săptămână, la ceva ce te drenează.",
      "Fă o pauză reală (fără ecran) la fiecare 90 de minute de lucru.",
      "Identifică un singur lucru care îți consumă energia și redu-l.",
    ],
    book: "„Burnout: The Secret to Unlocking the Stress Cycle” — Emily & Amelia Nagoski",
  },
  therapistCTA: {
    utmMedium: "test_burnout",
    triggerCategories: ["moderate", "severe"],
    blocks: {
      moderate: {
        headline: "Hai să vorbim cu cineva care înțelege.",
        subline:
          "Iată câțiva terapeuți potriviți pentru tine. Prima ședință e un test, tu decizi dacă continui.",
        ctaLabel: "Vezi toți terapeuții recomandați",
        energyParam: 2,
      },
      severe: {
        headline: "Nu trebuie să rezolvi singur/ă.",
        subline:
          "Iată câțiva terapeuți care au lucrat cu burnout. Prima ședință e un test, tu decizi dacă continui.",
        ctaLabel: "Găsește terapeutul potrivit",
        energyParam: 1,
      },
    },
  },
  conditionCode: {
    code: "QD85",
    name: "Burnout",
    system: "ICD-11",
  },
  meta: {
    title: "Test burnout: cât de epuizat ești, de fapt? | Mindtale",
    description:
      "Test validat clinic pentru burnout, bazat pe Copenhagen Burnout Inventory. 6 întrebări, 2 minute, gratuit. Adaptare RO revizuită de psihoterapeuți COPSI.",
    canonicalUrl: "https://mindtale.ro/teste/burnout",
    keywords: [
      "test burnout",
      "test burnout online",
      "sunt in burnout test",
      "epuizare profesionala test",
      "scala burnout",
      "copenhagen burnout inventory",
      "test burnout gratuit",
      "test burnout romana",
      "evaluare burnout",
      "cat de epuizat sunt",
    ],
  },
  stepperFootnote:
    'Răspunde la cum te simți în ultimele câteva săptămâni. Niciun răspuns nu e „corect" sau „greșit".',
  intro: {
    eyebrow: "6 întrebări · 2 minute · gratuit",
    title: "Răspunde sincer.\nE doar pentru tine.",
    subtitle:
      'Nu există răspunsuri „corecte". Fiecare întrebare e despre cum te simți tu, nu cum „ar trebui" să te simți.',
    ctaLabel: "Începe testul",
  },
  bandsHeader: "Cele trei zone (Kristensen et al., 2005)",
};
